import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Slider } from "./ui/slider";
import { MagnifyingGlassPlus } from "@phosphor-icons/react";

async function getCroppedBlob(imageSrc, cropPixels) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageSrc;
  });
  const size = Math.min(512, Math.round(cropPixels.width));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    cropPixels.x,
    cropPixels.y,
    cropPixels.width,
    cropPixels.height,
    0,
    0,
    size,
    size
  );
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.92));
}

export function ImageCropDialog({ open, onClose, imageSrc, onCropped, title = "Bild zuschneiden" }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_, pixels) => setAreaPixels(pixels), []);

  const confirm = async () => {
    if (!areaPixels) return;
    setBusy(true);
    try {
      const blob = await getCroppedBlob(imageSrc, areaPixels);
      await onCropped(blob);
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-sm p-0 overflow-hidden" data-testid="crop-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle className="text-lg font-bold text-[#0A1128]">{title}</DialogTitle>
        </DialogHeader>
        <div className="relative mt-3 h-64 w-full bg-[#0A1128]">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          )}
        </div>
        <div className="flex items-center gap-3 px-5 py-4">
          <MagnifyingGlassPlus size={20} className="shrink-0 text-[#8a6d2f]" />
          <Slider
            data-testid="crop-zoom-slider"
            value={[zoom]}
            min={1}
            max={3}
            step={0.01}
            onValueChange={(v) => setZoom(v[0])}
            className="flex-1"
          />
        </div>
        <div className="flex gap-2 border-t border-[#E2E8F0] p-4">
          <button
            data-testid="crop-cancel-btn"
            onClick={onClose}
            className="h-11 flex-1 rounded-full border border-[#E2E8F0] text-sm font-semibold text-[#4A5568] active:scale-[0.98]"
          >
            Abbrechen
          </button>
          <button
            data-testid="crop-confirm-btn"
            onClick={confirm}
            disabled={busy}
            className="h-11 flex-1 rounded-full bg-[#0A1128] text-sm font-semibold text-white active:scale-[0.98] disabled:opacity-60"
          >
            {busy ? "…" : "Übernehmen"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
